import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useI18n } from "../providers/I18nProvider";

// Tek bir HLS akışını oynatan oynatıcı.
//  - Safari/iOS: native HLS (<video src>).
//  - Diğer tarayıcılar: hls.js (MSE). xhrSetup ile same-origin cookie gönderilir.
//  - flip: doc 02 Adım 4'teki CSS transform (örn. "rotate(180deg)").
//
// Canlı yayın dayanıklılığı: tam ekran/sekme görünürlük değişiminde veya stall
// olduğunda otomatik olarak live-edge'e atlayıp tekrar oynatır (donma fix'i).
// src değişince/unmount'ta hls instance'ı temizlenir.
const HlsPlayer = ({ src, flip = "", muted = true, className = "", onError }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0); // "Tekrar dene" ile efekti yeniden çalıştırır
  const { t } = useI18n();

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;

    setFailed(false);
    let hls = null;
    const timers = [];

    const fail = (msg) => {
      setFailed(true);
      if (onError) onError(msg);
    };

    // Canlı kenara atla + oynat. Donma sonrası toparlanma için kullanılır.
    const resumeLive = () => {
      try {
        if (hls && typeof hls.liveSyncPosition === "number") {
          const target = hls.liveSyncPosition;
          if (target && video.currentTime < target - 5) {
            video.currentTime = target;
          }
          if (hls.media && hls.streamController?.state === "STOPPED") {
            hls.startLoad();
          }
        } else if (video.seekable && video.seekable.length) {
          const end = video.seekable.end(video.seekable.length - 1);
          if (end && video.currentTime < end - 5) video.currentTime = end;
        }
      } catch (_) {
        /* yoksay */
      }
      if (video.paused) video.play().catch(() => {});
    };

    const onVisOrFs = () => {
      if (!document.hidden) resumeLive();
    };
    document.addEventListener("fullscreenchange", onVisOrFs);
    document.addEventListener("webkitfullscreenchange", onVisOrFs);
    document.addEventListener("visibilitychange", onVisOrFs);
    video.addEventListener("pause", resumeLive);
    video.addEventListener("stalled", resumeLive);
    video.addEventListener("waiting", resumeLive);

    const canNative = video.canPlayType("application/vnd.apple.mpegurl");

    if (canNative && !Hls.isSupported()) {
      video.src = src;
      const onLoaded = () => video.play().catch(() => {});
      video.addEventListener("loadedmetadata", onLoaded);
      const onNativeError = () => fail("native error");
      video.addEventListener("error", onNativeError);
      return () => {
        document.removeEventListener("fullscreenchange", onVisOrFs);
        document.removeEventListener("webkitfullscreenchange", onVisOrFs);
        document.removeEventListener("visibilitychange", onVisOrFs);
        video.removeEventListener("pause", resumeLive);
        video.removeEventListener("stalled", resumeLive);
        video.removeEventListener("waiting", resumeLive);
        video.removeEventListener("loadedmetadata", onLoaded);
        video.removeEventListener("error", onNativeError);
        video.removeAttribute("src");
        video.load();
      };
    }

    if (Hls.isSupported()) {
      hls = new Hls({
        // Canlı kenara yakın kal; geri kalırsa hızlandırarak yakala.
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 10,
        maxLiveSyncPlaybackRate: 1.5,
        lowLatencyMode: false,
        backBufferLength: 10,
        xhrSetup: (xhr) => {
          xhr.withCredentials = true; // auth + cookieCheck cookie'lerini gönder
        },
      });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              // Ağ koptuğunda sıkı döngüye girmesin: 2 sn sonra yeniden dene
              timers.push(setTimeout(() => hls && hls.startLoad(), 2000));
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              fail(`${data.type}: ${data.details}`);
              hls.destroy();
          }
        }
      });
      return () => {
        document.removeEventListener("fullscreenchange", onVisOrFs);
        document.removeEventListener("webkitfullscreenchange", onVisOrFs);
        document.removeEventListener("visibilitychange", onVisOrFs);
        video.removeEventListener("pause", resumeLive);
        video.removeEventListener("stalled", resumeLive);
        video.removeEventListener("waiting", resumeLive);
        timers.forEach(clearTimeout);
        hlsRef.current = null;
        hls.destroy();
      };
    }

    fail("HLS desteklenmiyor");
    return () => {
      document.removeEventListener("fullscreenchange", onVisOrFs);
      document.removeEventListener("webkitfullscreenchange", onVisOrFs);
      document.removeEventListener("visibilitychange", onVisOrFs);
    };
  }, [src, onError, attempt]);

  return (
    <div className={`hls-player ${className}`}>
      <video
        ref={videoRef}
        muted={muted}
        autoPlay
        playsInline
        controls={false}
        style={flip ? { transform: flip } : undefined}
      />
      {failed && (
        <div className="hls-player-error" role="alert">
          <span>{t("cctv.streamFailed")}</span>
          <button
            type="button"
            className="hls-retry"
            onClick={(e) => {
              e.stopPropagation(); // hücre tıklaması (odaklanma) tetiklenmesin
              setAttempt((a) => a + 1);
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {t("cctv.retry")}
          </button>
        </div>
      )}
    </div>
  );
};

export default HlsPlayer;
